// =====================================================================
// src/pages/intl-acca/SlipDetailDocumentPage.tsx
// =====================================================================
// Direction B of the Slip Detail surface — "probability document"
// editorial reading layout. One leg per card; each reasoning note
// reads as a paragraph in Playfair Display. Same data contract as
// SlipDetailPage.tsx (Direction A — the dense pricing-audit terminal).
//
// Routes: rendered conditionally inside IntlAccaSlipRoute based on a
// user preference (localStorage key `strikza:intlacca:slip-direction`,
// mirrors the super-acca pattern at src/pages/super-acca/SuperAccaPage.tsx).

import type {
  IntlAccaSlipWithLegs,
  IntlAccaTournament,
} from "../../db/intl-acca-types";
import { formatClvPp, formatOdds, legStage } from "../../db/intl-acca-types";
import { TournamentStateBanner } from "../../components/intl-acca/TournamentStateBanner";
import { TierLockedTeaser } from "../../components/intl-acca/TierLockedTeaser";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";
import { CLVConfidencePill } from "../../components/intl-acca/CLVConfidencePill";

export interface SlipDetailDocumentPageProps {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  slip: IntlAccaSlipWithLegs | null;
  matchLabels?: Record<string, string>;
  hasAccess: boolean;
  onUpgrade?: () => void;
  onShare?: () => void;
  onPlace?: () => void;
}

export function SlipDetailDocumentPage(props: SlipDetailDocumentPageProps): JSX.Element {
  const { loading, error, tournament, slip, matchLabels, hasAccess, onUpgrade, onShare, onPlace } = props;

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading slip…</div>
      </div>
    );
  }
  if (error || !slip) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState
          variant="custom"
          title="Slip not found."
          body={error ?? "The slip id does not exist or you do not have access."}
        />
      </div>
    );
  }

  const gate =
    slip.slip_type === "FOCUS" ? "focus_acca" :
    slip.slip_type === "VALUE" ? "value_acca" :
    "power_acca";

  const body = (
    <>
      {/* Hero */}
      <section
        style={{
          padding: "56px var(--sz-space-7) var(--sz-space-7)",
          maxWidth: 920,
          margin: "0 auto",
        }}
      >
        <div className="ia-eyebrow">
          {slip.slip_type} Acca · {slip.legs.length} legs · {slip.id.slice(0, 8)}
        </div>
        <h1
          style={{
            fontFamily: "var(--sz-font-head)",
            fontSize: "var(--sz-head-xl)",
            fontWeight: 500,
            lineHeight: 1.02,
            marginTop: 22,
            letterSpacing: "-0.015em",
          }}
        >
          {slip.legs.length} matchday signals,
          <br />
          <em style={{ color: "var(--accent)", fontStyle: "italic" }}>
            priced before close.
          </em>
        </h1>
        <p
          className="ia-sub"
          style={{ marginTop: 28, fontSize: "var(--sz-body-lg)", maxWidth: 680 }}
        >
          Each leg below is recorded against the closing line — win or lose,
          every selection generates a CLV data point. The reasoning note is
          the why; the audit columns are the proof.
        </p>

        {/* Numeric summary strip */}
        <div
          style={{
            marginTop: 36,
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 18,
            padding: "20px 0",
            borderTop: "1px solid var(--border-faint)",
            borderBottom: "1px solid var(--border-faint)",
          }}
        >
          <Summary label="COMBINED" value={`${formatOdds(slip.total_odds)}×`} amber />
          <Summary label="LEGS"     value={String(slip.legs.length)} />
          <Summary
            label="SLIP CLV"
            value={slip.clv_score_final == null ? "—" : formatClvPp(slip.clv_score_final)}
            amber={slip.clv_score_final !== null}
            emptyLabel={slip.clv_score_final == null ? "Awaits closes" : undefined}
          />
          <Summary
            label="STATUS"
            value={slip.status}
            tone={
              slip.status === "WON" ? "pos" :
              slip.status === "LOST" ? "neg" :
              slip.status === "VOID" ? "ghost" : "amber"
            }
          />
        </div>
      </section>

      {/* Stacked leg cards */}
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 var(--sz-space-7) var(--sz-space-7)" }}>
        {slip.legs.length === 0 ? (
          <HonestEmptyState
            variant="custom"
            title="Slip has no legs."
            body="This slip exists but has no legs persisted yet. Check the orchestrator publish job."
          />
        ) : (
          slip.legs.map((leg, i) => {
            const stage = legStage(leg);
            return (
              <article
                key={leg.id}
                style={{
                  marginTop: 28,
                  background: "var(--bg-elev)",
                  border: "1px solid var(--border)",
                  padding: 36,
                  borderRadius: "var(--sz-radius-lg)",
                  position: "relative",
                }}
              >
                <span
                  className="ia-mono"
                  style={{
                    position: "absolute",
                    top: -14,
                    left: 36,
                    background: "var(--bg)",
                    padding: "0 12px",
                    fontSize: 10,
                    color: "var(--accent)",
                    letterSpacing: "0.22em",
                  }}
                >
                  LEG · {String(i + 1).padStart(2, "0")} OF {String(slip.legs.length).padStart(2, "0")}
                </span>

                <header
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 24,
                  }}
                >
                  <div>
                    <div
                      className="ia-mono"
                      style={{
                        fontSize: "var(--sz-mono-xs)",
                        color: "var(--text-tertiary)",
                        letterSpacing: "0.18em",
                      }}
                    >
                      {matchLabels?.[leg.match_id] ?? leg.match_id}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--sz-font-head)",
                        fontSize: "var(--sz-head-md)",
                        fontWeight: 500,
                        marginTop: 8,
                        color: "var(--text)",
                      }}
                    >
                      <span style={{ color: "var(--accent)" }}>{leg.selection}</span>
                    </div>
                  </div>
                  <CLVConfidencePill confidence={leg.clv_confidence} clv_pp={leg.clv_pp} />
                </header>

                {/* Selection / odds block */}
                <div
                  style={{
                    margin: "8px 0 24px",
                    padding: "var(--sz-space-4) var(--sz-space-5)",
                    background: "var(--bg)",
                    border: "1px solid var(--border-faint)",
                    borderRadius: "var(--sz-radius-md)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div className="ia-metric-label">STRIKZA / MARKET / CLOSE</div>
                    <div
                      className="ia-mono"
                      style={{
                        marginTop: 8,
                        fontSize: 14,
                        color: "var(--text)",
                        letterSpacing: 0.5,
                      }}
                    >
                      {formatOdds(leg.strikza_price)}
                      <span style={{ color: "var(--text-tertiary)", margin: "0 8px" }}>/</span>
                      {formatOdds(leg.market_price_at_selection)}
                      <span style={{ color: "var(--text-tertiary)", margin: "0 8px" }}>/</span>
                      {leg.closing_price == null ? (
                        <span style={{ color: "var(--text-tertiary)" }}>—</span>
                      ) : (
                        formatOdds(leg.closing_price)
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="ia-metric-label">EDGE</div>
                    <div
                      className="ia-mono"
                      style={{
                        fontSize: 14,
                        marginTop: 8,
                        color: leg.implied_edge == null ? "var(--text-tertiary)" : "var(--accent-text)",
                      }}
                    >
                      {leg.implied_edge == null ? "—" : `+${leg.implied_edge.toFixed(2)}pp`}
                    </div>
                  </div>
                </div>

                {/* Reasoning as PRD §12 demands — never hidden */}
                <p
                  style={{
                    fontFamily: "var(--sz-font-head)",
                    fontSize: 18,
                    lineHeight: 1.55,
                    color: "var(--text)",
                    maxWidth: 760,
                    textWrap: "pretty",
                  }}
                >
                  {leg.reasoning_note}
                </p>

                {stage === "unsettled" ? (
                  <div
                    className="ia-clv-cell-empty"
                    style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid var(--border-faint)" }}
                  >
                    CLV settles at kickoff · benchmark Pinnacle
                  </div>
                ) : null}
              </article>
            );
          })
        )}
      </div>

      {/* Footer CTA */}
      <footer
        style={{
          maxWidth: 920,
          margin: "40px auto 56px",
          padding: "40px var(--sz-space-7)",
          borderTop: "2px solid var(--sz-amber-dim)",
          borderBottom: "2px solid var(--sz-amber-dim)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 32,
        }}
      >
        <div>
          <div className="ia-metric-label">SLIP COMBINED</div>
          <div
            style={{
              fontFamily: "var(--sz-font-display)",
              fontSize: 80,
              color: "var(--accent)",
              lineHeight: 1,
              marginTop: 6,
            }}
          >
            {formatOdds(slip.total_odds)}×
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
          {onPlace ? (
            <button type="button" onClick={onPlace} style={solidBtn}>Place the slip →</button>
          ) : null}
          {onShare ? (
            <button type="button" onClick={onShare} style={ghostBtn}>Generate share card</button>
          ) : null}
        </div>
      </footer>
    </>
  );

  return (
    <div className="ia-root">
      {tournament ? <TournamentStateBanner tournament={tournament} /> : null}
      {hasAccess ? (
        body
      ) : (
        <div className="ia-page-pad">
          <TierLockedTeaser gate={gate} onUpgrade={onUpgrade}>{body}</TierLockedTeaser>
        </div>
      )}
    </div>
  );
}

function Summary({
  label, value, amber, emptyLabel, tone,
}: {
  label: string;
  value: string;
  amber?: boolean;
  emptyLabel?: string;
  tone?: "pos" | "neg" | "amber" | "ghost";
}): JSX.Element {
  const color =
    tone === "pos" ? "var(--pos)" :
    tone === "neg" ? "var(--neg)" :
    tone === "ghost" ? "var(--text-tertiary)" :
    amber || tone === "amber" ? "var(--accent)" :
    "var(--text)";
  return (
    <div>
      <div className="ia-metric-label">{label}</div>
      {emptyLabel ? (
        <div className="ia-metric-value-empty" style={{ marginTop: 6 }}>{emptyLabel}</div>
      ) : (
        <div
          style={{
            fontFamily: "var(--sz-font-display)",
            fontSize: 24,
            color,
            marginTop: 6,
            lineHeight: 1,
          }}
        >
          {value}
        </div>
      )}
    </div>
  );
}

const solidBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--sz-text-inverse)",
  border: "none",
  borderRadius: "var(--sz-radius-md)",
  padding: "14px 28px",
  fontFamily: "var(--sz-font-mono)",
  fontSize: 12,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  fontWeight: 600,
  cursor: "pointer",
};
const ghostBtn: React.CSSProperties = {
  background: "transparent",
  color: "var(--text)",
  border: "1px solid var(--border-strong)",
  borderRadius: "var(--sz-radius-md)",
  padding: "14px 28px",
  fontFamily: "var(--sz-font-mono)",
  fontSize: 12,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  cursor: "pointer",
};
